import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../api';

const MESES = [
  '',
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre'
];

export default function InformeIvaOperacionesPage() {
  const now = new Date();
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [anio, setAnio] = useState(now.getFullYear());
  const [cfg, setCfg] = useState(null);
  const [audit, setAudit] = useState([]);
  const [digest, setDigest] = useState(null);
  const [auditFilter, setAuditFilter] = useState('');
  const [busy, setBusy] = useState(false);

  const loadCfg = useCallback(async () => {
    const { data } = await api.get('/informes-iva/config/global');
    setCfg(data);
  }, []);

  const loadAudit = useCallback(async () => {
    const { data } = await api.get('/informes-iva/audit', { params: { limit: 120 } });
    setAudit(data || []);
  }, []);

  useEffect(() => {
    loadCfg().catch((e) => toast.error(e.response?.data?.error || e.message));
    loadAudit().catch((e) => toast.error(e.response?.data?.error || e.message));
  }, [loadCfg, loadAudit]);

  const saveCfg = async () => {
    if (!cfg) return;
    setBusy(true);
    try {
      await api.put('/informes-iva/config/global', {
        digestMode: cfg.digestMode,
        emailOficina: cfg.emailOficina,
        frescuraHoras: cfg.frescuraHoras,
        autoSyncOnRun: cfg.autoSyncOnRun,
        syncF29OnRun: cfg.syncF29OnRun,
        scheduledRunsEnabled: cfg.scheduledRunsEnabled,
        autoSendGlobalEnabled: cfg.autoSendGlobalEnabled,
        permitirRemanenteCeroAsumido: cfg.permitirRemanenteCeroAsumido
      });
      toast.success('Configuración global guardada');
      await loadCfg();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const cargarDigest = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/informes-iva/digest/${anio}/${mes}`);
      setDigest(data);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const ejecutarCron = async () => {
    if (!window.confirm('¿Ejecutar ahora el cron de informes programados (según frecuencia por cliente)?')) return;
    setBusy(true);
    try {
      const { data } = await api.post('/informes-iva/jobs/scheduled-run');
      toast.success(`Cron ejecutado: ${data.processed ?? 0} clientes procesados`);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const auditFiltrada = audit.filter((row) => {
    if (!auditFilter.trim()) return true;
    const q = auditFilter.toLowerCase();
    return `${row.action} ${row.entityType} ${row.meta ? JSON.stringify(row.meta) : ''}`.toLowerCase().includes(q);
  });

  if (!cfg) {
    return <div className="max-w-4xl mx-auto p-8 text-slate-500">Cargando operaciones…</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-10 space-y-10">
      <div>
        <p className="text-sm text-slate-500">
          <Link to="/informes-iva" className="text-indigo-600 hover:underline">
            ← Panel del mes
          </Link>
        </p>
        <h1 className="text-2xl font-bold text-slate-900 mt-1">Operaciones Informes IVA</h1>
        <p className="text-sm text-slate-600 mt-2">
          Automatización, auditoría y digest mensual. SMTP y envíos al cliente están en{' '}
          <Link to="/informes-iva/correo" className="text-indigo-600 underline">
            Correo y envíos
          </Link>
          .
        </p>
      </div>

      <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4 text-sm">
        <h2 className="font-semibold text-slate-900">Automatización y sync</h2>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(cfg.scheduledRunsEnabled)}
            onChange={(e) => setCfg({ ...cfg, scheduledRunsEnabled: e.target.checked })}
          />
          Cron diario: generar informes según frecuencia en config. por cliente
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(cfg.autoSyncOnRun)}
            onChange={(e) => setCfg({ ...cfg, autoSyncOnRun: e.target.checked })}
          />
          Sync SII al pulsar Informe IVA (RCV + honorarios + formulario F29)
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={cfg.syncF29OnRun !== false}
            onChange={(e) => setCfg({ ...cfg, syncF29OnRun: e.target.checked })}
          />
          Incluir lectura formulario F29 en el SII (504, 562, 142…)
        </label>
        <label className="block max-w-xs">
          Frescura datos (horas)
          <input
            type="number"
            className="w-full border rounded-lg px-3 py-2 mt-1"
            value={cfg.frescuraHoras ?? 48}
            onChange={(e) => setCfg({ ...cfg, frescuraHoras: Number(e.target.value) })}
          />
        </label>
        <label className="block max-w-md">
          Modo digest oficina
          <select
            className="w-full border rounded-lg px-3 py-2 mt-1"
            value={cfg.digestMode || 'POR_ASIGNADO'}
            onChange={(e) => setCfg({ ...cfg, digestMode: e.target.value })}
          >
            <option value="POR_ASIGNADO">Por contador asignado</option>
            <option value="GLOBAL_OFICINA">Un correo global oficina</option>
            <option value="AMBOS">Ambos</option>
          </select>
        </label>
        <div className="flex flex-wrap gap-2 pt-2">
          <button
            type="button"
            disabled={busy}
            onClick={saveCfg}
            className="px-4 py-2 bg-slate-900 text-white rounded-lg disabled:opacity-50"
          >
            Guardar
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={ejecutarCron}
            className="px-4 py-2 border border-slate-300 rounded-lg disabled:opacity-50"
          >
            Ejecutar cron ahora (manual)
          </button>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4 text-sm">
        <h2 className="font-semibold text-slate-900">Digest del mes</h2>
        <div className="flex flex-wrap gap-3 items-end">
          <label>
            Mes
            <select className="block border rounded-lg px-3 py-2 mt-1" value={mes} onChange={(e) => setMes(Number(e.target.value))}>
              {MESES.map((m, i) =>
                i === 0 ? null : (
                  <option key={i} value={i}>
                    {m}
                  </option>
                )
              )}
            </select>
          </label>
          <label>
            Año
            <input
              type="number"
              className="block border rounded-lg px-3 py-2 mt-1 w-24"
              value={anio}
              onChange={(e) => setAnio(Number(e.target.value))}
            />
          </label>
          <button type="button" disabled={busy} onClick={cargarDigest} className="px-4 py-2 border rounded-lg">
            Generar vista digest
          </button>
        </div>
        {digest && (
          <div className="border rounded-lg p-4 bg-slate-50 max-h-96 overflow-auto text-xs font-mono whitespace-pre-wrap">
            {JSON.stringify(digest.resumen ?? digest, null, 2)}
          </div>
        )}
      </section>

      <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4 text-sm">
        <div className="flex flex-wrap justify-between gap-3 items-center">
          <h2 className="font-semibold text-slate-900">Auditoría</h2>
          <button type="button" onClick={loadAudit} className="text-indigo-600 underline text-xs">
            Actualizar
          </button>
        </div>
        <input
          type="search"
          placeholder="Filtrar por acción, entidad…"
          className="w-full border rounded-lg px-3 py-2"
          value={auditFilter}
          onChange={(e) => setAuditFilter(e.target.value)}
        />
        <div className="border rounded-lg overflow-hidden max-h-[420px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 sticky top-0">
              <tr>
                <th className="text-left p-2">Fecha</th>
                <th className="text-left p-2">Acción</th>
                <th className="text-left p-2">Detalle</th>
              </tr>
            </thead>
            <tbody>
              {auditFiltrada.map((row) => (
                <tr key={row._id} className="border-t border-slate-100">
                  <td className="p-2 whitespace-nowrap text-slate-500">
                    {new Date(row.createdAt).toLocaleString('es-CL')}
                  </td>
                  <td className="p-2 font-medium">{row.action}</td>
                  <td className="p-2 text-slate-600 break-all">
                    {row.entityType}
                    {row.meta ? ` · ${JSON.stringify(row.meta)}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {auditFiltrada.length === 0 && <p className="p-4 text-slate-500 text-center">Sin registros</p>}
        </div>
      </section>
    </div>
  );
}
